import { Modifier } from '@dnd-kit/core'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function toChunks({
  array = [],
  chunkSize,
}: {
  array?: Array<any>
  chunkSize: number
}) {
  return (
    array?.reduce?.((accumulator: Array<any>, item, index) => {
      const chunkIndex = Math.floor(index / chunkSize) as number

      if (!accumulator[chunkIndex]) {
        accumulator[chunkIndex] = []
      }

      accumulator[chunkIndex].push(item)
      return accumulator
    }, []) || []
  )
}

export function createSnapModifier({
  gridDimensions,
  getGridContainer,
  getZoom,
}: {
  gridDimensions: { width: number; height: number }
  getGridContainer: () => Element | null
  getZoom: () => number
}): Modifier {
  // Capture a stable origin on first invocation of a drag.
  let origin: { baseX: number; baseY: number; zoom: number } | null = null
  const EPS = 1e-6

  return ({ transform, activeNodeRect }) => {
    const gridContainer = getGridContainer()
    const zoom = getZoom?.() ?? 1
    if (!gridContainer || !activeNodeRect || zoom <= 0) return transform

    const { width: cellW, height: cellH } = gridDimensions
    if (cellW <= 0 || cellH <= 0) return transform

    // Initialize once per drag (first frame)
    if (!origin) {
      const gridRect = gridContainer.getBoundingClientRect()
      origin = {
        baseX: (activeNodeRect.left - gridRect.left) / zoom,
        baseY: (activeNodeRect.top - gridRect.top) / zoom,
        zoom,
      }
    }

    const z = origin.zoom // stable zoom for the drag
    const rawTX = transform.x || 0
    const rawTY = transform.y || 0

    /**
     * Invariant:
     * A pure click (no drag) should never move the element.
     *
     * Why:
     * For elements with fractional base coordinates, snapping `0,0` transform
     * to a 1x1 grid can create a small non-zero transform (e.g. -0.03px),
     * which looks like a "jump on click". We keep the original transform while
     * the pointer delta is effectively zero so snapping only starts after a
     * real drag movement.
     */
    if (Math.abs(rawTX) <= EPS && Math.abs(rawTY) <= EPS) {
      return transform
    }

    const txM = rawTX / z // transform in content space
    const tyM = rawTY / z

    // Snap to nearest grid line relative to the stable base
    const targetX = Math.round((origin.baseX + txM) / cellW + EPS) * cellW
    const targetY = Math.round((origin.baseY + tyM) / cellH + EPS) * cellH

    const snapTXM = targetX - origin.baseX
    const snapTYM = targetY - origin.baseY

    return { ...transform, x: snapTXM * z, y: snapTYM * z }
  }
}

export function isEmptyOrSpaces(str: string) {
  return str.trim().length === 0
}

export function toMB(bytes: number) {
  return bytes / 1024 / 1024
}
